// Global Instructions Rule Applied!
//
// Configuration → Statement Types services: the admin read and the per-row
// save (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md i.7).
//
// Same fetch conventions as emailCategoriesConfigService. Both routes are
// admin-gated at the backend AND inside the RPC. A refusal (a report that does
// not fit the type, a label too long, a locked field) comes back 400 with the
// RPC's own sentence - a designed, user-facing outcome the panel shows in its
// Alert.
import { z } from 'zod'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  StatementTypeAdminRow,
  validateStatementTypeAdminRow
} from '../../../state_manager/data_models/statement_types_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// The backend's error envelope key is `message` (handle_route_custom_error);
// `error` is the auth guard's.
const ErrorBodySchema = z.object({ message: z.string().optional(), error: z.string().optional() })

// Reading `message` FIRST is what makes the 403 and the refusals surface
// readably (#232).
async function readJson(response: Response): Promise<unknown> {
  const body: unknown = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    const parsed = ErrorBodySchema.safeParse(body)
    const said = parsed.success ? parsed.data.message || parsed.data.error : undefined
    throw new Error(said || `Request failed (${response.status})`)
  }
  return body
}

/** The live type rows, in display order, validated before they are returned. */
export const fetchStatementTypesConfig = async (accessToken: string | null): Promise<StatementTypeAdminRow[]> => {
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STATEMENT_TYPES_CONFIG), {
    method: API_ROUTES.CONFIG.STATEMENT_TYPES_CONFIG.method,
    headers: authHeaders(accessToken)
  })
  const body = await readJson(response)
  const rows = typeof body === 'object' && body !== null && 'data' in body ? body.data : []
  return validateStatementTypeAdminRow(rows ?? [], { isArray: true })
}

export interface StatementTypeFields {
  label?: string
  short_label?: string | null
  display_order?: number | null
  document_report_code?: string
}

/** One type row. Only the supplied keys are touched - the RPC reads an absent
 *  key as "leave unchanged", which lets the panel save a single field. Writes
 *  use the house request envelope { data: {...} } (the /config/* routes unwrap
 *  it via _json_body; a flat body is silently dropped, #232). */
export const updateStatementType = async (
  accessToken: string | null,
  typeId: number,
  fields: StatementTypeFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STATEMENT_TYPE_UPDATE, { typeId }),
    {
      method: API_ROUTES.CONFIG.STATEMENT_TYPE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}
